if (!customElements.get('product-modal')) {
  // Apple's rubber band: the further past the edge, the less the photo follows the finger
  const rubberband = (offset, dimension) => (offset * dimension * 0.55) / (dimension + 0.55 * Math.abs(offset));

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('[role="document"]');
        this.phone = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.offset = 0;

        // On phones CSS fits each photo inside the screen from its ratio, so the box is right before it loads
        this.content.querySelectorAll(':scope > img[width][height]').forEach((img) => {
          img.style.setProperty('--odma-ratio', img.getAttribute('width') / img.getAttribute('height'));
        });

        // Dawn closes the media modal on mouse release only; a tap on the dark area closes it too.
        // On click rather than pointerup, so the tap cannot land on the page once the modal is gone.
        this.addEventListener('click', (event) => {
          const darkArea = [this, this.content, this.content.parentElement].includes(event.target);
          if (darkArea && this.hasAttribute('open') && !this.dragged) this.hide();
        });

        // Phones: drag the photo down to close
        this.content.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.content.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.content.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.content.addEventListener('pointercancel', (event) => this.onPointerUp(event));
        this.phone.addEventListener('change', () => this.reset());
        this.onZoom = () => this.classList.toggle('is-zoomed', window.visualViewport.scale > 1.01);
      }

      disconnectedCallback() {
        if (this.isConnected) return; // Dawn moves the modal to <body>; only a real removal (theme editor) gets here
        this.reset();
        this.unlockScroll();
        window.visualViewport?.removeEventListener('resize', this.onZoom);
      }

      hide() {
        this.unlockScroll();
        super.hide();
        this.reset();
        window.visualViewport?.removeEventListener('resize', this.onZoom);
        this.classList.remove('is-zoomed');
      }

      show(opener) {
        this.reset();
        this.lockScroll();
        super.show(opener);
        this.showActiveMedia();
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onZoom);
          this.onZoom();
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      // iOS ignores overflow: hidden for touch scrolling, so on touch screens the page is pinned where it
      // is while the lightbox is open (as the quiz does) and put back exactly there on close
      lockScroll() {
        if (this.lockedY != null || !window.matchMedia('(pointer: coarse)').matches) return;
        this.lockedY = window.scrollY;
        const top = `-${this.lockedY}px`;
        Object.assign(document.body.style, { position: 'fixed', top, left: '0', right: '0', width: '100%' });
      }

      unlockScroll() {
        if (this.lockedY == null) return;
        const y = this.lockedY;
        this.lockedY = null;
        Object.assign(document.body.style, { position: '', top: '', left: '', right: '', width: '' });
        if (!y) return;
        // Contract C1: html[data-odma-jump] tells the sticky header to ignore this programmatic scroll
        // (the restore is an instant scrollTo, so two frames are enough; no scrollend needed)
        const root = document.documentElement;
        root.dataset.odmaJump = '';
        const behavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, y);
        root.style.scrollBehavior = behavior;
        requestAnimationFrame(() => requestAnimationFrame(() => delete root.dataset.odmaJump));
      }

      onPointerDown(event) {
        if (this.drag) {
          // a second finger means a pinch, not a swipe
          if (event.pointerId !== this.drag.id) this.cancelDrag();
          return;
        }
        this.dragged = false;
        if (!event.isPrimary || event.pointerType === 'mouse' || !this.phone.matches) return;
        if (window.visualViewport && window.visualViewport.scale > 1.01) return; // zoomed in: one finger pans
        if (event.target.closest('deferred-media, product-model')) return; // video and 3D take their own drags
        this.media = this.content.querySelector(':scope > .active');
        if (!this.media) return;
        const moving = this.raf != null;
        cancelAnimationFrame(this.raf);
        this.raf = null;
        const samples = [{ t: event.timeStamp, y: event.clientY }];
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, from: this.offset, samples };
        if (moving) this.startDrag(event); // grabbed mid-flight: it follows the finger from where it is
      }

      onPointerMove(event) {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.hypot(dx, dy) < 10) return; // wait for a clear direction
          if (Math.abs(dx) > Math.abs(dy)) {
            this.drag = null; // sideways: not a swipe down
            return;
          }
          this.startDrag(event);
        }
        drag.samples.push({ t: event.timeStamp, y: event.clientY });
        if (drag.samples.length > 10) drag.samples.shift();
        this.render(drag.from + dy);
      }

      startDrag(event) {
        this.drag.active = true;
        this.dragged = true;
        this.exit = window.innerHeight - this.media.offsetTop; // offset that puts the photo below the screen
        this.content.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }

      onPointerUp(event) {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        if (!drag.active || event.type === 'pointercancel') {
          this.cancelDrag(); // a tap (the click handler decides) or the browser took over
          return;
        }
        this.drag = null;
        // release speed: the last 100 ms of movement, or 0 if the finger rested before lifting
        const last = drag.samples[drag.samples.length - 1];
        const first = drag.samples.find((sample) => last.t - sample.t <= 100);
        const span = (last.t - first.t) / 1000;
        const velocity = event.timeStamp - last.t < 100 && span > 0 ? (last.y - first.y) / span : 0;
        // Apple's projection: where the photo would coast to at that speed. Far enough down closes;
        // anything else, or a finger moving back up, returns it.
        const projected = this.offset + ((velocity / 1000) * 0.998) / (1 - 0.998);
        if (velocity > -100 && projected > 120) {
          this.animate(this.exit + window.innerHeight / 4, velocity, (y) => y >= this.exit, () => this.hide());
        } else {
          this.animate(0, velocity, () => false, () => this.reset());
        }
      }

      cancelDrag() {
        const active = this.drag && this.drag.active;
        this.drag = null;
        if (active) this.animate(0, 0, () => false, () => this.reset());
      }

      // Critically damped spring (Apple's default: damping 1, response 0.3 s) that starts where the photo is,
      // at the finger's speed, so grabbing it again or letting go never jumps. Reduced motion: a 150 ms fade.
      animate(target, velocity, isDone, onDone) {
        const from = this.offset;
        const v0 = Number.isFinite(velocity) ? velocity : 0;
        const w = (2 * Math.PI) / 0.3;
        const b = v0 + w * (from - target);
        const start = performance.now();
        const step = (now) => {
          const t = (now - start) / 1000;
          let y;
          let settled;
          if (this.reducedMotion.matches) {
            const k = Math.min(t / 0.15, 1);
            y = from + (target - from) * k;
            settled = k === 1;
          } else {
            const decay = Math.exp(-w * t);
            y = target + (from - target + b * t) * decay;
            settled = Math.abs(y - target) < 0.5 && Math.abs((v0 - w * b * t) * decay) < 10;
          }
          this.render(settled ? target : y);
          if (settled || isDone(y) || t > 1) {
            this.raf = null;
            onDone();
          } else {
            this.raf = requestAnimationFrame(step);
          }
        };
        this.raf = requestAnimationFrame(step);
      }

      render(offset) {
        this.offset = offset;
        const y = offset < 0 ? rubberband(offset, window.innerHeight) : offset;
        const dim = Math.max(0, 1 - Math.max(y, 0) / this.exit);
        this.style.setProperty('--odma-lightbox-dim', dim.toFixed(3)); // the page shows through as the photo leaves
        if (this.reducedMotion.matches) {
          this.media.style.opacity = Math.min(1, dim * 2).toFixed(3); // nothing moves; the photo fades out at the end
        } else {
          this.media.style.transform = `translate3d(0, ${y}px, 0)`;
        }
      }

      reset() {
        cancelAnimationFrame(this.raf);
        this.raf = null;
        this.drag = null;
        this.offset = 0;
        this.classList.remove('is-dragging');
        this.style.removeProperty('--odma-lightbox-dim');
        this.media?.style.removeProperty('transform');
        this.media?.style.removeProperty('opacity');
      }
    }
  );
}
